import React, { useState } from 'react';

import { FullScreenModal } from '@opendoor/bricks/complex/Modal';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Card, Flex, Link, Text } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { Sentry } from '@opendoor/observability';

import { useAcceptListingLead } from '../../graphql/athena/mutations/AcceptListingLead';
import { useRejectListingLead } from '../../graphql/athena/mutations/RejectListingLead';
import {
  getReadableCityFromParnLead,
  getReadableExpirationFromParnLead,
  getReadableStreetFromParnLead,
  ParnLead,
} from '../../helpers/getAllParnLeadData';
import ParnLeadCardContent from './ParnLeadCardContent';
import { ParnLeadDetailsPage } from './ParnLeadDetailsPage';
import { getDisplayStateFromParnLead } from './parnUtils';

/**
 * Renders the summary card for an Agent's lead
 *
 * storybook-check-ignore
 */
interface ParnLeadCardProps {
  name?: string;
  agentLead: ParnLead;
  agentCustomerUuid: string;
}

/** Card that displays lead information for an agent*/
export const ParnLeadCard: React.FC<ParnLeadCardProps> = ({
  name,
  agentLead,
  agentCustomerUuid,
}) => {
  const { isMobile } = useDevice();
  const handleClose = () => {
    setOpen(false);
  };

  const displayState = getDisplayStateFromParnLead(agentLead);
  const pendingAssignmentAcceptance = agentLead.parnData.pendingAssignmentAcceptance;
  const readableStreet = getReadableStreetFromParnLead(agentLead.addressData);
  const readableCity = getReadableCityFromParnLead(agentLead.addressData);
  const [isOpen, setOpen] = useState(false);
  const agentLeadId = agentLead.parnData?.agentLeadId ?? '';
  const proposalExpirationTime = getReadableExpirationFromParnLead(
    agentLead.parnData.proposalExpiration,
  );
  const {
    acceptLead,
    loading: loadingAcceptListingLead,
    error: acceptListingLeadError,
  } = useAcceptListingLead();
  const handleAcceptLead = async () => {
    try {
      await acceptLead(agentCustomerUuid, agentLeadId);
      showToast('info', 'Successfully accepted lead', 'accept-lead-toast', {
        toastOptions: { autoClose: 3000 },
      });
    } catch (error) {
      showToast('error', 'Error accepting lead', 'accept-lead-toast', {
        toastOptions: { autoClose: false },
      });
      if (acceptListingLeadError) {
        Sentry.withScope((scope) => {
          scope.setExtra('agent_lead', agentLead);
          scope.setExtra('error', JSON.stringify(error));
          Sentry.captureException(error);
        });
      }
    }
  };
  const {
    rejectLead,
    loading: loadingRejectListingLead,
    error: rejectListingLeadError,
  } = useRejectListingLead();
  const handleRejectLead = async () => {
    try {
      await rejectLead(agentCustomerUuid, agentLeadId);
      showToast('info', 'Successfully declined lead', 'reject-lead-toast', {
        toastOptions: { autoClose: 3000 },
      });
    } catch (error) {
      showToast('error', 'Error rejecting lead', 'reject-lead-toast', {
        toastOptions: { autoClose: false },
      });
      if (rejectListingLeadError) {
        Sentry.withScope((scope) => {
          scope.setExtra('agent_lead', agentLead);
          scope.setExtra('error', JSON.stringify(error));
          Sentry.captureException(error);
        });
      }
    }
  };

  return (
    <Card
      variant="secondary"
      display="flex"
      flexDir="row"
      boxShadow="unset"
      border="1px solid"
      borderColor="neutrals40"
      borderRadius={isMobile ? 'semiRounded' : 'roundedSmooth'}
      padding="24px"
    >
      <Flex
        justifyContent="flex-start"
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          padding: '0',
          display: 'flex',
          width: '100%',
        }}
      >
        {/* Customer data */}
        <ParnLeadCardContent
          isMobile={isMobile}
          fieldName="Customer"
          fieldValue1={name}
          style={{
            flex: 1,
            minWidth: '113px',
            marginRight: '15px',
          }}
        />
        <ParnLeadCardContent
          isMobile={isMobile}
          fieldName="Address"
          fieldValue1={readableStreet}
          fieldValue2={readableCity}
          style={{ flex: 1, minWidth: '150px', marginRight: '15px' }}
        />
        <ParnLeadCardContent
          isMobile={isMobile}
          fieldName="Status"
          fieldValue1={displayState}
          style={{ flex: 0.65, minWidth: '98px' }}
        />
        <Flex
          gridGap="3"
          flexDirection="column"
          justifyContent={isMobile ? 'center' : 'right'}
          style={{ flex: 1, minWidth: '150px', alignItems: isMobile ? '' : 'flex-end' }}
        >
          {pendingAssignmentAcceptance ? (
            <Flex gridGap="inherit" flexDirection="column">
              <Flex gridGap="3" style={{ flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  analyticsName="parn-decline-lead"
                  variant="secondary"
                  onClick={handleRejectLead}
                  aria-label="Decline"
                  size={isMobile ? 'md' : 'sm'}
                  style={{
                    order: isMobile ? 2 : 1,
                    width: isMobile ? '' : '80px',
                    height: isMobile ? '' : '36px',
                    lineHeight: 1.5,
                  }}
                  loading={loadingRejectListingLead}
                >
                  Decline
                </Button>
                <Button
                  analyticsName="parn-accept-lead"
                  variant="primary"
                  onClick={handleAcceptLead}
                  aria-label="Accept"
                  size={isMobile ? 'md' : 'sm'}
                  style={{
                    order: isMobile ? 1 : 2,
                    width: isMobile ? '' : '80px',
                    height: isMobile ? '' : '36px',
                    lineHeight: 1.5,
                  }}
                  loading={loadingAcceptListingLead}
                >
                  Accept
                </Button>
              </Flex>
              <Text
                size={'s00'}
                style={{
                  color: '#6E6E6E',
                  textAlign: isMobile ? 'center' : 'right',
                  paddingTop: '5px',
                }}
              >
                {`Expires at ${proposalExpirationTime}`}
              </Text>
            </Flex>
          ) : (
            <Flex
              textAlign="right"
              flexDirection="column"
              justifyContent="center"
              style={{ flex: 1 }}
              gridGap="3"
            >
              <Link
                aria-label=""
                analyticsName="parn-show-details"
                onClick={() => setOpen(true)}
                width="100%"
              >
                <Text
                  size={'110'}
                  fontWeight={'medium'}
                  style={{
                    color: '#0D76d6',
                    paddingTop: '1px',
                    textAlign: isMobile ? 'center' : 'right',
                  }}
                >
                  Show details
                </Text>
              </Link>
            </Flex>
          )}
          <FullScreenModal
            isOpen={isOpen}
            analyticsName="parn-details-modal"
            title={'Lead Details'}
            onRequestClose={handleClose}
          >
            <ParnLeadDetailsPage
              agentLead={agentLead}
              displayState={displayState}
              readableStreet={readableStreet}
              readableCity={readableCity}
              isMobile={isMobile}
            />
          </FullScreenModal>
        </Flex>
      </Flex>
    </Card>
  );
};
